import React, { useState, useCallback, useRef } from "react";
import { Modal, Spinner, Alert } from "react-bootstrap";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import ItemService from "../../../../../services/customer_appCommonService";

// ─── Required headers (must match template exactly) ───────────────────────────
const REQUIRED_HEADERS = [
  "Product Code",
  "Category",
  "Material Sub Category",
  "Product Name",
  "HSN Code",
  "Location",
  "Quantity",
  "Unit",
  "Target Category",
  "Critical Product",
  "Brand",
  "Product Type",
  "Discarded",
  "Top Selling",
  "Warranty",
  "Mfg. By",
  "Mkt By",
  "Barcode",
];

// ─── Preview columns (show raw data as-is) ────────────────────────────────────
const PREVIEW_COLUMNS = REQUIRED_HEADERS.map((h) => ({
  name: h,
  selector: (row) =>
    row[h] !== undefined && row[h] !== "" ? String(row[h]) : "—",
  sortable: true,
  wrap: true,
  width:
    h === "Product Name" || h === "Material Sub Category" ? "200px" : "140px",
}));

// Columns for the duplicates result table
const DUPLICATE_COLUMNS = [
  {
    name: "#",
    selector: (row, index) => index + 1,
    cell: (row, index) => (
      <span className="fw-bold text-muted">{index + 1}</span>
    ),
    width: "60px",
  },
  {
    name: "Barcode",
    selector: (row) => row,
    cell: (row) => <span className="fw-semibold text-danger">{row}</span>,
    sortable: true,
  },
];

// Constrained columns — backend is case-sensitive on these
const COLUMN_RULES = [
  {
    field: "Location",
    allowed: ["Rent", "Sale"],
    required: true,
    label: "Location (Godown)",
  },
  {
    field: "Critical Product",
    allowed: ["Yes", "No"],
    required: false,
    label: "Critical Product",
  },
  {
    field: "Discarded",
    allowed: ["Yes", "No"],
    required: false,
    label: "Discarded",
  },
  {
    field: "Top Selling",
    allowed: ["Yes", "No"],
    required: false,
    label: "Top Selling",
  },
];

const validateExcelData = (rows) => {
  const errors = [];

  COLUMN_RULES.forEach(({ field, allowed, required, label }) => {
    const badRows = [];
    const badValues = new Set();

    rows.forEach((row, index) => {
      const raw = row[field];
      const val =
        raw !== undefined && raw !== null && raw !== ""
          ? String(raw).trim()
          : "";

      if (val === "") {
        if (required) {
          badRows.push(index + 2); // +2 because row 1 is the header
          badValues.add("(empty)");
        }
        return;
      }

      if (!allowed.includes(val)) {
        badRows.push(index + 2);
        badValues.add(`"${val}"`);
      }
    });

    if (badRows.length > 0) {
      const rowList =
        badRows.length <= 5
          ? badRows.join(", ")
          : `${badRows.slice(0, 3).join(", ")} ... (${badRows.length} rows total)`;

      errors.push({
        label,
        badValues: [...badValues].join(", "),
        rowList,
        allowed,
      });
    }
  });

  return errors;
};

// ─── Component ────────────────────────────────────────────────────────────────
const BarcodeBulkUpload = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { total, inserted, duplicates[] }
  const [dataErrors, setDataErrors] = useState([]);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setRawRows([]);
    setError("");
    setShowPreview(false);
    setUploadResult(null);
    setDataErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ── 1. Read file → validate headers → show preview ───────────────────────
  const handleFileChange = useCallback(async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (!selected.name.match(/\.(xlsx|xls)$/i)) {
      setError("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    reset();
    setFile(selected);
    setError("");

    try {
      const buffer = await selected.arrayBuffer();
      const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // Read ONLY the first row to validate headers — cheap, fast
      const [headerRow] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        range: 0,
        defval: "",
      });

      const fileHeaders = (headerRow ?? []).map((h) => String(h).trim());
      const missing = REQUIRED_HEADERS.filter((h) => !fileHeaders.includes(h));
      const extra = fileHeaders.filter(
        (h) => h !== "" && !REQUIRED_HEADERS.includes(h),
      );

      if (missing.length > 0) {
        setError(
          `❌ Missing columns: ${missing.join(", ")}.\n` +
            `Please download the template and use the correct column headers.`,
        );
        setFile(null);
        return;
      }

      // Warn about unexpected extra columns (non-blocking)
      if (extra.length > 0) {
        setError(
          `⚠️ Unknown extra columns detected (will be ignored): ${extra.join(", ")}`,
        );
      }

      // Read full data for preview only
      const allRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (allRows.length === 0) {
        setError("❌ File has headers but no data rows.");
        setFile(null);
        return;
      }

      setRawRows(allRows);
      setDataErrors(validateExcelData(allRows));
      setShowPreview(true);
    } catch (err) {
      setError("Failed to read file: " + err.message);
      setFile(null);
    }
  }, []);

  // ── 2. Upload raw file directly — backend handles all processing ──────────
  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("timeZone", "Asia/Calcutta");

      const res = await ItemService.uploadBarcodeData(formData);

      // if (res?.status === 200) {
      //   // Backend sends duplicates under "duplicateBarcodes"
      //   const duplicates =
      //     res?.data?.duplicateBarcodes || res?.data?.duplicateBarcodes || [];

      //   if (duplicates.length > 0) {
      //     // Stay in modal — show results view with duplicate list
      //     setUploadResult({
      //       total: res?.data?.total ?? rawRows.length,
      //       inserted: res?.data?.inserted ?? rawRows.length - duplicates.length,
      //       duplicates,
      //     });
      //   } else {
      //     toast.success(res?.message || "Bulk upload successful");
      //     onSuccess?.();
      //     reset();
      //     onHide();
      //   }
      // AFTER:
      if (res?.status === 200) {
        const duplicates = res?.data?.duplicateBarcodes || [];
        // Always show results view — success or partial
        setUploadResult({
          total: res?.data?.total ?? rawRows.length,
          inserted: res?.data?.inserted ?? rawRows.length - duplicates.length,
          duplicates,
        });
      } else {
        throw new Error(res?.message || "Upload failed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Upload failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleResultOk = () => {
    onSuccess?.();
    reset();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        reset();
        onHide();
      }}
      centered
      backdrop="static"
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {uploadResult ? "Upload Complete" : "Barcode Bulk Upload"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {/* ── RESULTS VIEW ── shown after upload when duplicates exist */}
        {uploadResult ? (
          <div>
            {/* Summary cards */}
            <div className="d-flex gap-3 mb-4">
              <div className="flex-fill text-center p-3 rounded border">
                <div className="fs-4 fw-bold text-primary">
                  {uploadResult.total}
                </div>
                <div className="small text-muted">Total Rows</div>
              </div>
              <div className="flex-fill text-center p-3 rounded border">
                <div className="fs-4 fw-bold text-success">
                  {uploadResult.inserted}
                </div>
                <div className="small text-muted">Inserted</div>
              </div>
              <div className="flex-fill text-center p-3 rounded border">
                <div className="fs-4 fw-bold text-warning">
                  {uploadResult.duplicates.length}
                </div>
                <div className="small text-muted">Duplicates Skipped</div>
              </div>
            </div>
            {/* Duplicates list */}
            {/* <Alert variant="warning" className="mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>
                {uploadResult.duplicates.length} barcode
                {uploadResult.duplicates.length !== 1 ? "s" : ""} already exist
                in the system and were skipped.
              </strong>{" "}
              Duplicate uploads are not allowed.
            </Alert>

            <DataTable
              title="Rejected Barcodes"
              columns={DUPLICATE_COLUMNS}
              data={uploadResult.duplicates}
              pagination
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationPerPage={10}
              highlightOnHover
              striped
              dense
              fixedHeader
              fixedHeaderScrollHeight="300px"
            /> */}
            {uploadResult.duplicates.length === 0 ? (
              <Alert variant="success" className="mb-3">
                <i className="bi bi-check-circle-fill me-2"></i>
                <strong>
                  All {uploadResult.inserted} barcode
                  {uploadResult.inserted !== 1 ? "s" : ""} uploaded
                  successfully.
                </strong>
              </Alert>
            ) : (
              <>
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>
                    {uploadResult.duplicates.length} barcode
                    {uploadResult.duplicates.length !== 1 ? "s" : ""} already
                    exist in the system and were skipped.
                  </strong>{" "}
                  Duplicate uploads are not allowed.
                </Alert>

                <DataTable
                  title="Rejected Barcodes"
                  columns={DUPLICATE_COLUMNS}
                  data={uploadResult.duplicates}
                  pagination
                  paginationRowsPerPageOptions={[10, 20, 50]}
                  paginationPerPage={10}
                  highlightOnHover
                  striped
                  dense
                  fixedHeader
                  fixedHeaderScrollHeight="300px"
                />
              </>
            )}
          </div>
        ) : (
          /* ── UPLOAD FORM VIEW ── normal state */
          <>
            {error && (
              <Alert
                variant={error.startsWith("⚠️") ? "warning" : "danger"}
                className="py-2"
                style={{ whiteSpace: "pre-line" }}
              >
                {error}
              </Alert>
            )}

            {/* ── Upload guidelines for the user ── */}
            <div
              className="p-3 mb-3 rounded border border-warning"
              style={{ backgroundColor: "#fffbea", fontSize: "0.85rem" }}
            >
              <p className="fw-bold mb-2" style={{ color: "#856404" }}>
                <i className="bi bi-info-circle me-1"></i>
                Before uploading, please follow these rules:
              </p>
              <ul
                className="mb-0 ps-3"
                style={{ color: "#5a4a00", lineHeight: "1.8" }}
              >
                <li>
                  <strong>Empty values</strong> — Leave the cell{" "}
                  <strong>blank</strong>. Do <u>not</u> type <code>NA</code>,{" "}
                  <code>N/A</code>, <code>Null</code>, or <code>-</code>.
                </li>
                <li>
                  <strong>Yes / No columns</strong> — Use exactly{" "}
                  <code>Yes</code> or <code>No</code> (capital first letter
                  only). Do <u>not</u> use <code>YES</code>, <code>NO</code>,{" "}
                  <code>yes</code>, or <code>no</code>.
                </li>
                <li>
                  <strong>Location (Godown) column</strong> — Use exactly{" "}
                  <code>Rent</code> or <code>Sale</code>. Do <u>not</u> use{" "}
                  <code>RENT</code>, <code>SALE</code>, <code>rent</code>, or{" "}
                  <code>sale</code>.
                </li>
                <li>
                  <strong>Column names</strong> — The first row must match the
                  template headers exactly (spelling and casing). Do not rename
                  or remove any column.
                </li>
              </ul>
            </div>

            <label className="form-label fw-bold">Select Excel File</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={uploading}
            />

            {file && (
              <div className="mt-2 p-2 bg-light rounded d-flex align-items-center gap-2">
                <i className="bi bi-file-earmark-excel text-success"></i>
                <small className="text-success fw-semibold">{file.name}</small>
                <small className="text-muted ms-1">
                  ({(file.size / 1024).toFixed(2)} KB)
                </small>
              </div>
            )}

            {/* Validation errors — shown after file is parsed */}
            {dataErrors.length > 0 && (
              <div className="mt-3">
                <Alert variant="danger" className="mb-0">
                  <p className="fw-bold mb-2">
                    <i className="bi bi-x-octagon-fill me-2"></i>
                    Fix these errors before uploading:
                  </p>
                  <ul className="mb-0 ps-3" style={{ lineHeight: "2" }}>
                    {dataErrors.map((err, i) => (
                      <li key={i}>
                        <strong>{err.label}:</strong> Found {err.badValues} in
                        row(s) {err.rowList}. Allowed:{" "}
                        {err.allowed.map((v, j) => (
                          <span key={v}>
                            {j > 0 && " or "}
                            <code>{v}</code>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </Alert>
              </div>
            )}

            {/* Raw data preview */}
            {showPreview && rawRows.length > 0 && (
              <div className="mt-4">
                <h6 className="fw-bold">File Preview</h6>
                <DataTable
                  columns={PREVIEW_COLUMNS}
                  data={rawRows}
                  pagination
                  paginationRowsPerPageOptions={[5, 10, 20]}
                  paginationPerPage={5}
                  highlightOnHover
                  striped
                  dense
                  fixedHeader
                  fixedHeaderScrollHeight="350px"
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {uploadResult ? (
          /* Results view footer — only OK button */
          <button className="btn btn-primary px-4" onClick={handleResultOk}>
            <i className="bi bi-check-lg me-1"></i>OK
          </button>
        ) : (
          /* Upload form footer */
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                reset();
                onHide();
              }}
              disabled={uploading}
            >
              <i className="bi bi-x-circle me-1"></i>Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              // disabled={uploading || !file || rawRows.length === 0}
              disabled={
                uploading ||
                !file ||
                rawRows.length === 0 ||
                dataErrors.length > 0
              }
            >
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-upload me-1"></i>
                  Upload
                </>
              )}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BarcodeBulkUpload;
